import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { stateSchema } from "~/schemas/SignUp/Location";
import { Dropdown } from "~/shared/components/ui/Dropdown";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { US_STATES } from "~/shared/utils/constants";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

const stateOptions: { value: string; label: string }[] = US_STATES.map(
  ({ label }) => ({
    label,
    value: label,
  }),
);

interface StateFormProps {
  onPrev: () => void;
  onNext: (response?: string) => void;
}

export const StateForm = ({
  onPrev,
  onNext,
  stepId = "state",
}: StateFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { state } = patient;

  const defaultValue = state;
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: "location",
    subStep: "state", // see table below
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof stateSchema>>({
    mode: "onBlur",
    resolver: zodResolver(stateSchema),
    defaultValues: {
      state: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("state", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const onSubmit: SubmitHandler<z.infer<typeof stateSchema>> = (data) => {
    setPatientField("state", data.state);
    trackComplete({ state: data.state });
    onNext(data.state);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("state", "");
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">
            What state do you currently live in?
          </h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <Dropdown
            control={control}
            options={stateOptions}
            {...register("state")}
            onValueChange={() => {
              // Frontier: picking a state advances; on revisit, wait for Next.
              if (autoAdvanceOnSelect) {
                void handleSubmit(onSubmit, onError)();
              }
            }}
          />
        </div>
        {errors.state && <ErrorMessage message={errors.state.message ?? ""} />}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
